import { useState, useEffect, useRef } from "react";
import axios from "axios";

type Suggestion = {
  placeId: string;
  mainText: string;
  secondaryText: string;
};

type AddressAutocompleteProps = {
  setValue: (name: string, value: any) => void;
};

export default function AddressAutocomplete({ setValue }: AddressAutocompleteProps) {
  const [input, setInput] = useState("");
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [sessionToken] = useState(() => crypto.randomUUID()); // stable for this session
  const suppressNextSearch = useRef(false);

  useEffect(() => {
    if (suppressNextSearch.current) {
      suppressNextSearch.current = false;
      setSuggestions([]);
      return;
    }

    const handler = setTimeout(async () => {
      if (input.length < 8) {
        setSuggestions([]);
        return;
      }

      try {
        const { data } = await axios.get("/places", {
          params: {
            q: input,
            sessionToken,
            languageCode: "en",
          },
        });

        setSuggestions(data.suggestions || []);
      } catch (e) {
        console.error("Autocomplete error:", e);
        setSuggestions([]);
      }
    }, 500);

    return () => clearTimeout(handler);
  }, [input, sessionToken]);

  const selectSuggestion = async (s: Suggestion) => {
    try {
      const { data } = await axios.get("/places", {
        params: { placeId: s.placeId, languageCode: "en" },
      });

      // Backend already returns structured fields
      setValue("Address", data.address);
      setValue("City", data.city);
      setValue("State", data.state);
      setValue("PostalCode", data.postalCode);

      if (!data.countryCode) {
        console.error("Missing ISO country code:", data);
      }

      setValue("Country", data.countryCode || "");

      suppressNextSearch.current = true;
      setSuggestions([]);
      setInput(data.address || s.mainText);

      // Clear suggestions and update input field
      setSuggestions([]);
      suppressNextSearch.current = true; // prevent immediate re-search
      setInput(data.address || s.mainText);
    } catch (e) {
      console.error("Place details error:", e);
    }
  };

  return (
    <div className="relative">
      <input
        className="text-input"
        type="text"
        placeholder="Search and select your address"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        autoComplete="off"
      />

      {suggestions.length > 0 && (
        <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded shadow">
          {suggestions.map((s) => (
            <li key={s.placeId} className="px-3  cursor-pointer hover:bg-gray-100" onClick={() => selectSuggestion(s)}>
              {`${s.mainText}, ${s.secondaryText}`}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
